import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ExternalLink, Check } from 'lucide-react';
import { getPlatformLogo } from './PlatformLogos';

interface Platform {
  id: string;
  name: string;
  badge: string;
  conversions: number;
  averageTicket: string;
  revenue: string;
  since: string;
  isFavorite: boolean;
}

interface ConnectedPlatformsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platforms: Platform[];
  containerRef?: React.RefObject<HTMLDivElement>;
}

export function ConnectedPlatformsModal({ open, onOpenChange, platforms }: ConnectedPlatformsModalProps) {
  useLanguage();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        key="connected-platforms-modal-v3-premium" 
        className="max-w-[600px] overflow-hidden flex flex-col p-0 bg-white rounded-[18px] shadow-[0_10px_35px_rgba(0,0,0,0.08)] [&>button]:hidden"
      >
        <DialogHeader className="px-7 pt-7 pb-5 border-b border-[#E5E7EB]">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-semibold text-[#111827] mb-1">
                {translateInterfaceText("Plataformas conectadas")}</DialogTitle>
              <p className="text-[13px] text-[#6B7280]">
                {translateInterfaceText("Gerencie suas plataformas de venda integradas")}</p>
            </div>
            <a 
              href="/tracking" 
              target="_blank"
              className="text-[#007BFF] hover:text-[#0056b3] transition-colors text-[13px] font-medium flex items-center gap-1.5"
            >
              {translateInterfaceText("Ver integrações")}<ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </DialogHeader>
        
        <div className="space-y-[22px] overflow-y-auto flex-1 px-7 py-6 max-h-[60vh]">
          {platforms.map((platform, index) => {
            return (
              <div 
                key={platform.id}
                className="flex items-center justify-between px-5 py-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-[14px] transition-none"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 flex-shrink-0">
                    {getPlatformLogo(platform.id, 32)}
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right min-w-[80px]">
                    <p className="text-[#6B7280] text-[11px] uppercase tracking-wide mb-1 font-medium">{translateInterfaceText("Conversões")}</p>
                    <p className="font-semibold text-[15px] text-[#111827]">{platform.conversions}</p>
                  </div>
                  <div className="text-right min-w-[110px]">
                    <p className="text-[#6B7280] text-[11px] uppercase tracking-wide mb-1 font-medium">{translateInterfaceText("Ticket médio")}</p>
                    <p className="font-semibold text-[15px] text-[#111827]">
                      {platform.averageTicket}
                    </p>
                  </div>
                  <div className="text-right min-w-[120px] border-l border-[#E5E7EB] pl-6">
                    <p className="text-[#6B7280] text-[11px] uppercase tracking-wide mb-1 font-medium">{translateInterfaceText("Receita total")}</p>
                    <p className="font-semibold text-[16px] text-[#111827]">
                      {platform.revenue}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Resumo Geral */}
        <div className="mx-7 mb-7 pt-6 border-t border-[#E5E7EB] bg-[#F9FAFB] rounded-[14px] px-6 py-5">
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-[11px] text-[#6B7280] uppercase tracking-wide mb-2 font-medium">{translateInterfaceText("Conversões")}</p>
              <p className="text-2xl font-bold text-[#111827]">
                {platforms.reduce((acc, p) => acc + p.conversions, 0)}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-[#6B7280] uppercase tracking-wide mb-2 font-medium">{translateInterfaceText("Receita total")}</p>
              <p className="text-2xl font-bold text-[#111827]">
                R$ {(() => {
                  const total = platforms.reduce((acc, p) => {
                    const value = p.revenue.replace(/[^\d,]/g, '').replace(',', '.');
                    return acc + parseFloat(value);
                  }, 0);
                  return total.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                })()}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-[#6B7280] uppercase tracking-wide mb-2 font-medium">{translateInterfaceText("Melhor plataforma")}</p>
              <div className="flex items-center gap-2">
                {(() => {
                  const bestPlatform = platforms.reduce((prev, current) => 
                    current.conversions > prev.conversions ? current : prev
                  );
                  return (
                    <div className="w-8 h-8">
                      {getPlatformLogo(bestPlatform.id, 32)}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
